import { Center, Loader } from "@mantine/core";
import type { ComponentType } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

import { DashboardLayout } from "@/layouts";

import { GuestRoute, ProtectedRoute } from "./guards";
import { ROUTES } from "./routes";

const withLazyLoad = (
  importFunc: () => Promise<{
    default: ComponentType<Record<string, unknown>>;
  }>,
) => {
  const LazyComponent = lazy(importFunc);

  return (
    <Suspense
      fallback={
        <Center h="100vh">
          <Loader />
        </Center>
      }
    >
      <LazyComponent />
    </Suspense>
  );
};

export const router = createBrowserRouter([
  // Public — accessible to everyone
  {
    path: "/",
    element: withLazyLoad(() => import("@/pages/landing/landing")),
  },
  {
    path: ROUTES.CHANGE_LOG,
    element: withLazyLoad(() => import("@/pages/changeLog/changeLog")),
  },

  // Guest-only — authenticated users are bounced to /profile
  {
    element: <GuestRoute />,
    children: [
      {
        path: ROUTES.SIGNUP,
        element: withLazyLoad(() => import("@/pages/auth/request-otp/request-otp")),
      },
      {
        path: ROUTES.LOGIN,
        element: withLazyLoad(() => import("@/pages/auth/request-otp/request-otp")),
      },
      {
        path: ROUTES.VERIFY_SIGNUP,
        element: withLazyLoad(() => import("@/pages/auth/verify-otp/verify-otp")),
      },
      {
        path: ROUTES.VERIFY_LOGIN,
        element: withLazyLoad(() => import("@/pages/auth/verify-otp/verify-otp")),
      },
    ],
  },

  // Protected — unauthenticated users are redirected to /login
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          {
            path: ROUTES.PROFILE,
            element: withLazyLoad(() => import("@/pages/profile/Profile-page")),
          },
          {
            path: ROUTES.PAYMENTS,
            element: withLazyLoad(() => import("@/pages/payment")),
          },
          {
            path: ROUTES.WALLET_LEDGER,
            element: withLazyLoad(() => import("@/pages/paymentLedger")),
          },
          {
            path: ROUTES.REPORTS,
            element: withLazyLoad(() => import("@/pages/reports")),
          },
          {
            path: ROUTES.REPORTS_RESULT,
            element: withLazyLoad(() => import("@/pages/reportResult")),
          },
          {
            path: ROUTES.DEVELOPERS,
            element: withLazyLoad(() => import("@/pages/developers")),
          },
          {
            path: ROUTES.SERVICE_DETAIL,
            element: withLazyLoad(() => import("@/pages/service-detail/service-detail")),
          },
          {
            path: ROUTES.WALLET_TOP_UP,
            element: withLazyLoad(() => import("@/pages/walletTopUp")),
          },
          {
            path: ROUTES.WALLET_TOP_UP_RESULT,
            element: withLazyLoad(() => import("@/pages/walletTopUpResult")),
          },
          {
            path: ROUTES.WALLET_TOP_UP_RESULT_WITH_ID,
            element: withLazyLoad(() => import("@/pages/walletTopUpResult")),
          },
          {
            path: ROUTES.MY_BUSINESSES,
            element: withLazyLoad(() => import("@/pages/my_businesses/my_businesses")),
          },
          // {
          //   path: ROUTES.EXCEL_EXPORTS,
          //   element: withLazyLoad(() => import("@/pages/excel-exports/excel-exports")),
          // },
        ],
      },
    ],
  },

  // Catch-all
  {
    path: "*",
    element: withLazyLoad(() => import("@/pages/errors/notFound")),
  },
]);
